/* assets/bstory-categories.js
 * Bstory — Homepage Categories carousel.
 *
 * Standalone custom element. It does not import, extend or depend on any Kalles
 * slider component, and adds no third-party library.
 *
 * The track is a native CSS scroll-snap list, so touch swipe, trackpad and
 * keyboard scrolling all work with no JavaScript at all. This file only adds
 * the two arrow buttons and their disabled state.
 *
 * RTL note: `element.scrollLeft` is famously inconsistent across engines in RTL
 * (negative, reversed, or zero-based depending on the browser). Nothing here
 * reads or writes scrollLeft. Navigation works purely on DOM index plus
 * getBoundingClientRect, and moves with scrollIntoView({ inline: 'start' }),
 * which is direction-aware. That makes the same code correct in RTL and LTR
 * without reversing the DOM or the block loop.
 */

const TAG_NAME = 'bstory-categories-slider';

if (!customElements.get(TAG_NAME)) {
  customElements.define(
    TAG_NAME,
    class BstoryCategoriesSlider extends HTMLElement {
      connectedCallback() {
        this._retries = 0;
        this._onScroll = this._onScroll.bind(this);
        this._onResize = this._onResize.bind(this);
        this._onPrev = this._onPrev.bind(this);
        this._onNext = this._onNext.bind(this);
        this._onBlockSelect = this._onBlockSelect.bind(this);
        this._init();
      }

      disconnectedCallback() {
        this._teardown();
      }

      _init() {
        this.viewport = this.querySelector('[data-bstory-categories-viewport]');
        this.track = this.querySelector('[data-bstory-categories-track]');
        this.prevButton = this.querySelector('[data-bstory-categories-prev]');
        this.nextButton = this.querySelector('[data-bstory-categories-next]');

        // The element can upgrade before its children are parsed. Retry on the
        // next frame, bounded so a genuinely empty section never loops.
        if (!this.viewport || !this.track) {
          if (this._retries < 10) {
            this._retries += 1;
            this._raf = requestAnimationFrame(() => this._init());
          }
          return;
        }

        this.viewport.addEventListener('scroll', this._onScroll, { passive: true });
        window.addEventListener('resize', this._onResize, { passive: true });
        if (this.prevButton) this.prevButton.addEventListener('click', this._onPrev);
        if (this.nextButton) this.nextButton.addEventListener('click', this._onNext);

        if (window.Shopify && window.Shopify.designMode) {
          document.addEventListener('shopify:block:select', this._onBlockSelect);
        }

        // Blocks can be added, removed or reordered in the Theme Editor.
        this._observer = new MutationObserver(() => this._update());
        this._observer.observe(this.track, { childList: true });

        this._update();
      }

      _teardown() {
        if (this._raf) cancelAnimationFrame(this._raf);
        if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
        if (this.viewport) this.viewport.removeEventListener('scroll', this._onScroll);
        window.removeEventListener('resize', this._onResize);
        if (this.prevButton) this.prevButton.removeEventListener('click', this._onPrev);
        if (this.nextButton) this.nextButton.removeEventListener('click', this._onNext);
        document.removeEventListener('shopify:block:select', this._onBlockSelect);
        if (this._observer) this._observer.disconnect();
      }

      get slides() {
        return Array.from(this.querySelectorAll('[data-bstory-categories-slide]'));
      }

      /* Index of the slide nearest the viewport's leading edge, measured
       * geometrically so it is identical in RTL and LTR. */
      _leadingIndex() {
        const slides = this.slides;
        if (!slides.length) return 0;

        const view = this.viewport.getBoundingClientRect();
        const isRtl = getComputedStyle(this).direction === 'rtl';
        // Leading edge = right edge in RTL, left edge in LTR.
        const edge = isRtl ? view.right : view.left;

        let bestIndex = 0;
        let bestDistance = Infinity;

        slides.forEach((slide, index) => {
          const rect = slide.getBoundingClientRect();
          const slideEdge = isRtl ? rect.right : rect.left;
          const distance = Math.abs(slideEdge - edge);
          if (distance < bestDistance) {
            bestDistance = distance;
            bestIndex = index;
          }
        });

        return bestIndex;
      }

      /* How many slides are fully inside the viewport right now. */
      _visibleCount() {
        const slides = this.slides;
        if (!slides.length) return 0;

        const view = this.viewport.getBoundingClientRect();
        const tolerance = 2;
        let count = 0;

        slides.forEach((slide) => {
          const rect = slide.getBoundingClientRect();
          if (rect.left >= view.left - tolerance && rect.right <= view.right + tolerance) {
            count += 1;
          }
        });

        return Math.max(count, 1);
      }

      _scrollToIndex(index) {
        const slides = this.slides;
        if (!slides.length) return;

        const clamped = Math.min(Math.max(index, 0), slides.length - 1);
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        slides[clamped].scrollIntoView({
          behavior: reduceMotion ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'start'
        });
      }

      _onPrev() {
        this._scrollToIndex(this._leadingIndex() - 1);
      }

      _onNext() {
        this._scrollToIndex(this._leadingIndex() + 1);
      }

      _onScroll() {
        if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
        this._scrollRaf = requestAnimationFrame(() => this._update());
      }

      _onResize() {
        this._update();
      }

      _onBlockSelect(event) {
        if (!this.contains(event.target)) return;
        const index = this.slides.indexOf(event.target);
        if (index > -1) this._scrollToIndex(index);
      }

      _setDisabled(button, disabled) {
        if (!button) return;
        button.disabled = disabled;
        button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      }

      /* Arrows stay visible even when nothing can move (the Figma composition
       * always shows both), but take a real disabled state. */
      _update() {
        const total = this.slides.length;
        if (!total) {
          this._setDisabled(this.prevButton, true);
          this._setDisabled(this.nextButton, true);
          return;
        }

        const leading = this._leadingIndex();
        const visible = this._visibleCount();

        this._setDisabled(this.prevButton, leading <= 0);
        this._setDisabled(this.nextButton, leading + visible >= total);
      }
    }
  );
}
